import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    Output,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { RubberElementInput } from './interfaces';
import { CanvasInteractionEvent } from "@pcp/core/interfaces";
import { RubberElementIframeComponent } from './rubber-element-iframe/iframe.component';

@Component({
    selector: 'pcp-rubber-element',
    templateUrl: './rubber-element.component.html',
    styleUrls: ['./rubber-element.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RubberElementIframeComponent]
})
export class RubberElementComponent implements AfterViewInit, OnDestroy {
    private readonly elementRef = inject(ElementRef<HTMLElement>);

    // Fixed iframe viewport: canvas/pointer coordinates stay in 0-1920 space regardless of
    // the visual size (mirrors HtmlElementBoardComponent, the sibling fixed-box element).
    private readonly fixedIframeWidth = 1920;
    private readonly fixedIframeHeight = 1080;

    // The inner iframe keeps the fixed viewport above, so canvas/pointer coordinates stay in
    // 0-1920 space; only the outer wrapper is visually scaled via a CSS transform. This avoids
    // the Safari/WebKit coordinate mis-mapping that the previous SVG <foreignObject> wrapper
    // caused. The wrapper is absolutely positioned and centered via translate(-50%, -50%); the
    // uniform scale fits the box into the host (contain).
    private readonly scale = signal(1);
    readonly scaleTransform = computed(() => `translate(-50%, -50%) scale(${this.scale()})`);

    private resizeObserver?: ResizeObserver;

    @Input() disableElementUsage: boolean;

    // messages/updates coming from the rubber image elements (user interactions).
    /**
     * Outputs user interactions on the rubber element:
     * - mouse cursor movements
     * - erased image parts
     */
    @Output() rubberElementInteraction = new Subject<CanvasInteractionEvent>();

    @Input() rubberImage!: RubberElementInput;

    ngAfterViewInit(): void {
        this.updateScaling();
        this.resizeObserver = new ResizeObserver(() => this.updateScaling());
        this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    reset() {
        // change object hash in order to reload the rubber element.
        this.rubberImage = { ...this.rubberImage };
    }

    /** Fits the fixed iframe box into the host (contain), keeping the 16:9 aspect ratio. */
    private updateScaling(): void {
        const { clientWidth, clientHeight } = this.elementRef.nativeElement;

        // Skip if the container has no size yet.
        if (clientWidth === 0 || clientHeight === 0) return;

        this.scale.set(Math.min(clientWidth / this.fixedIframeWidth, clientHeight / this.fixedIframeHeight));
    }
}
